import * as Sentry from '@sentry/react';

export function initSentry(): void {
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;

  if (!USE_SENTRY || !sentryDsn) {
    return;
  }

  Sentry.init({
    dsn: sentryDsn,
    release: VERSION,
    environment: ENVIRONMENT,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
      Sentry.replayIntegration(),
    ],

    /*
     * tracesSampleRate is set to 0 because we're using tracesSampler instead.
     * When tracesSampler is defined, it fully overrides tracesSampleRate.
     * This makes sampling logic centralized and more flexible.
     */
    tracesSampleRate: 0.0,

    tracesSampler: (ctx) => {
      /*
       * Page load transactions are typically the largest and most frequent.
       * Sampling only 5% significantly reduces Sentry traffic and rate limit risk.
       */
      if (ctx.transactionContext?.op === 'pageload') {
        return 0.05;
      }
      // Capture 20% of the transactions (devel, testing)
      if (ENVIRONMENT !== 'stable') {
        return 0.2;
      }
      // Capture 10% of the transactions (stable)
      return 0.1;
    },

    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [/^https:\/\/pluto3-devel\.itrack\.hu/, /^https:\/\/pluto3-devel\.itrack\.hu\/api/],

    /*
     * Session Replay records the user's interaction with the app (DOM, clicks, inputs, etc.).
     * We're not using Sentry Replay because session insights are monitored through Clarity.
     * Disabled (0.0) to reduce network traffic and avoid unnecessary event volume.
     */
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 0.0,

    ignoreErrors: [
      'Invalid LatLng object: (NaN, NaN)',
      'Request aborted',
      'timeout of 60000ms exceeded',
      'Network Error',
      'CanceledError: canceled',
      // Axios HTTP status error: 401–404, 500–504
      /^Request failed with status code (40[1-4]|50[0-4])$/,
      // Custom PlutoClientException errors
      /^PlutoClientException\b/,
    ],
  });
}
